// #region Imports
import { ApplicationRef, Injectable, inject } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, Subscription, catchError, of } from 'rxjs';
// #endregion Imports

// #region TypesAndInterfaces
/** Parte di navigator.connection (Network Information API) letta qui: non esiste in tutti i browser. */
interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

/** Rotta in attesa di essere scaricata: parte quando arriva il suo turno. */
interface WaitingPreload {
  start: () => void;
}

// Unico tipo di connessione su cui si scaricano in anticipo le pagine.
const FAST_CONNECTION_TYPE: string = '4g';
// Attesa massima di un momento libero del browser prima di scaricare comunque la rotta successiva.
const IDLE_TIMEOUT_MS: number = 2000;
// Pausa usata al posto di requestIdleCallback dove il browser non lo offre (Safari).
const IDLE_FALLBACK_DELAY_MS: number = 200;
// #endregion TypesAndInterfaces

/**
 * Precaricamento delle pagine lazy che non contende la rete con la pagina appena aperta.
 * Non scarica nulla se il browser chiede di risparmiare dati o la connessione non è veloce; altrimenti scarica
 * una rotta alla volta, solo quando l'app non ha richieste HTTP né navigazioni in corso e il browser è libero.
 * Una rotta con data.preload uguale a false non si scarica mai in anticipo. Le pagine non scaricate in anticipo
 * si scaricano, come sempre, al primo ingresso.
 */
@Injectable({ providedIn: 'root' })
export class NetworkAwarePreloadingStrategy implements PreloadingStrategy {
  // #region DependencyInjection
  private readonly applicationRef: ApplicationRef = inject(ApplicationRef);
  // #endregion DependencyInjection

  // #region State
  // Rotte proposte dal router e non ancora partite, nell'ordine in cui sono arrivate.
  private readonly waitingPreloads: WaitingPreload[] = [];
  // Rotta il cui file si sta scaricando: occupa l'unico posto disponibile.
  private currentPreload: WaitingPreload | null = null;
  // Vero mentre si aspetta il momento libero per far partire la rotta successiva.
  private isWaitingForQuietMoment: boolean = false;
  // #endregion State

  // #region PublicMethods
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (route.data?.['preload'] === false || !this.isConnectionFast()) {
      return of(null);
    }

    return new Observable<unknown>((subscriber) => {
      // Il router propone le rotte figlie solo dopo aver ricevuto il file della madre: se ne arriva una mentre una
      // rotta occupa il posto, quel file è già arrivato e il posto si libera, altrimenti la madre (che aspetta le
      // figlie per completare) e le figlie (che aspettano il posto) si attenderebbero a vicenda.
      if (this.currentPreload !== null) {
        this.release(this.currentPreload);
      }

      let loadSubscription: Subscription | undefined;
      const waitingPreload: WaitingPreload = {
        start: (): void => {
          this.currentPreload = waitingPreload;
          // Un errore di download non deve fermare il precaricamento delle altre rotte: la pagina si
          // scaricherà al primo ingresso, come se non fosse stata precaricata.
          loadSubscription = load().pipe(catchError((): Observable<null> => of(null))).subscribe({
            next: (value: unknown): void => {
              this.release(waitingPreload);
              subscriber.next(value);
            },
            complete: (): void => {
              this.release(waitingPreload);
              subscriber.complete();
            },
          });
        },
      };

      this.waitingPreloads.push(waitingPreload);
      this.startNextWhenQuiet();

      return (): void => {
        const waitingIndex: number = this.waitingPreloads.indexOf(waitingPreload);
        if (waitingIndex >= 0) {
          this.waitingPreloads.splice(waitingIndex, 1);
        }
        loadSubscription?.unsubscribe();
        this.release(waitingPreload);
      };
    });
  }
  // #endregion PublicMethods

  // #region PrivateMethods
  /** Libera il posto se lo occupa ancora questa rotta e prepara la partenza della successiva. */
  private release(preload: WaitingPreload): void {
    if (this.currentPreload !== preload) {
      return;
    }
    this.currentPreload = null;
    this.startNextWhenQuiet();
  }

  /** Fa partire la prima rotta in attesa quando il posto è libero, l'app è ferma e il browser è libero. */
  private startNextWhenQuiet(): void {
    if (this.currentPreload !== null || this.isWaitingForQuietMoment || this.waitingPreloads.length === 0) {
      return;
    }

    this.isWaitingForQuietMoment = true;
    void this.waitForQuietMoment().then((): void => {
      this.isWaitingForQuietMoment = false;
      if (this.currentPreload !== null) {
        return;
      }
      const nextPreload: WaitingPreload | undefined = this.waitingPreloads.shift();
      nextPreload?.start();
    });
  }

  /**
   * Si risolve quando l'app non ha richieste HTTP, navigazioni o disegni in sospeso (whenStable conta anche le
   * richieste di HttpClient) e poi il browser ha un momento libero.
   */
  private async waitForQuietMoment(): Promise<void> {
    await this.applicationRef.whenStable();
    await new Promise<void>((resolve: () => void): void => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback((): void => resolve(), { timeout: IDLE_TIMEOUT_MS });
      } else {
        setTimeout(resolve, IDLE_FALLBACK_DELAY_MS);
      }
    });
  }

  /** Falso se il browser chiede di risparmiare dati o dichiara una connessione più lenta del 4G. */
  private isConnectionFast(): boolean {
    const connection: NetworkInformation | undefined =
      (navigator as Navigator & { connection?: NetworkInformation }).connection;
    if (!connection) {
      // Il browser non dice com'è la connessione (Safari, Firefox): si precarica, una rotta alla volta.
      return true;
    }
    if (connection.saveData === true) {
      return false;
    }
    return connection.effectiveType === undefined || connection.effectiveType === FAST_CONNECTION_TYPE;
  }
  // #endregion PrivateMethods
}
